/**
 * FikoSPHVoxelKopru — SPH parçacık alanı ↔ voxel kafes köprüsü (Faz 0).
 *
 * İki iş:
 *  1. `kafestenSDF()` — dokum-kontrol voxel kafesinden (doluluk) işaretli mesafe
 *     alanı üretir (CPU chamfer). Kavite içi pozitif, dışı negatif → SPH SDF
 *     çarpışması parçacıkları kavitede tutar. Grid, dolum kafesiyle BİREBİR
 *     hizalıdır (yeniden örnekleme yok).
 *  2. Splat compute pass — her sim adımında parçacıkları voxel'lere damgalar
 *     (atomicMin fillTime ms). Sonu okunup CA `dolumZamani` formatına çevrilir.
 */
import { VOXEL_SPLAT_PASS, SPLAT_PARAMS_BOYUT } from './sph-voxel-splat.wgsl';

/** Köprü voxel grid tanımı — SPH uzayında (metre) */
export interface IKopruGrid {
  origin: [number, number, number];
  cellSize: number;
  boyut: [number, number, number];
}

export const DOLMADI_U32 = 0xffffffff;

/**
 * Voxel doluluk kafesinden işaretli mesafe alanı (SDF) üret — iki geçişli
 * 3D chamfer mesafe dönüşümü (ağırlıklar 1, √2, √3). Sınır hücreleri 0,
 * içerisi +mesafe, dışarısı −mesafe. Çıktı birimi = hucre × olcek
 * (mm kafes + olcek 0.001 → metre).
 */
export function kafestenSDF(
  doluluk: Uint8Array, boyut: [number, number, number], hucre: number, olcek: number,
): Float32Array {
  const [nx, ny, nz] = boyut;
  const nxy = nx * ny;
  const toplam = nxy * nz;
  const BUYUK = 1e9;
  const d = new Float32Array(toplam);

  // Sınır hücresi: 6-komşuluğunda zıt doluluk olan hücre → mesafe 0.5 (yarım hücre)
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      const st = nx * (y + ny * z);
      for (let x = 0; x < nx; x++) {
        const i = st + x;
        const v = doluluk[i];
        let sinir = x === 0 || y === 0 || z === 0 || x === nx - 1 || y === ny - 1 || z === nz - 1;
        if (!sinir) {
          sinir = doluluk[i - 1] !== v || doluluk[i + 1] !== v
            || doluluk[i - nx] !== v || doluluk[i + nx] !== v
            || doluluk[i - nxy] !== v || doluluk[i + nxy] !== v;
        }
        d[i] = sinir ? 0.5 : BUYUK;
      }
    }
  }

  const W1 = 1, W2 = Math.SQRT2, W3 = Math.sqrt(3);
  // İleri geçiş (z,y,x artan) — geride kalan 13 komşu
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      const st = nx * (y + ny * z);
      for (let x = 0; x < nx; x++) {
        const i = st + x;
        let m = d[i];
        if (x > 0 && d[i - 1] + W1 < m) m = d[i - 1] + W1;
        if (y > 0) {
          if (d[i - nx] + W1 < m) m = d[i - nx] + W1;
          if (x > 0 && d[i - nx - 1] + W2 < m) m = d[i - nx - 1] + W2;
          if (x < nx - 1 && d[i - nx + 1] + W2 < m) m = d[i - nx + 1] + W2;
        }
        if (z > 0) {
          const k = i - nxy;
          if (d[k] + W1 < m) m = d[k] + W1;
          if (x > 0 && d[k - 1] + W2 < m) m = d[k - 1] + W2;
          if (x < nx - 1 && d[k + 1] + W2 < m) m = d[k + 1] + W2;
          if (y > 0) {
            if (d[k - nx] + W2 < m) m = d[k - nx] + W2;
            if (x > 0 && d[k - nx - 1] + W3 < m) m = d[k - nx - 1] + W3;
            if (x < nx - 1 && d[k - nx + 1] + W3 < m) m = d[k - nx + 1] + W3;
          }
          if (y < ny - 1) {
            if (d[k + nx] + W2 < m) m = d[k + nx] + W2;
            if (x > 0 && d[k + nx - 1] + W3 < m) m = d[k + nx - 1] + W3;
            if (x < nx - 1 && d[k + nx + 1] + W3 < m) m = d[k + nx + 1] + W3;
          }
        }
        d[i] = m;
      }
    }
  }
  // Geri geçiş (z,y,x azalan) — ilerideki 13 komşu
  for (let z = nz - 1; z >= 0; z--) {
    for (let y = ny - 1; y >= 0; y--) {
      const st = nx * (y + ny * z);
      for (let x = nx - 1; x >= 0; x--) {
        const i = st + x;
        let m = d[i];
        if (x < nx - 1 && d[i + 1] + W1 < m) m = d[i + 1] + W1;
        if (y < ny - 1) {
          if (d[i + nx] + W1 < m) m = d[i + nx] + W1;
          if (x < nx - 1 && d[i + nx + 1] + W2 < m) m = d[i + nx + 1] + W2;
          if (x > 0 && d[i + nx - 1] + W2 < m) m = d[i + nx - 1] + W2;
        }
        if (z < nz - 1) {
          const k = i + nxy;
          if (d[k] + W1 < m) m = d[k] + W1;
          if (x < nx - 1 && d[k + 1] + W2 < m) m = d[k + 1] + W2;
          if (x > 0 && d[k - 1] + W2 < m) m = d[k - 1] + W2;
          if (y < ny - 1) {
            if (d[k + nx] + W2 < m) m = d[k + nx] + W2;
            if (x < nx - 1 && d[k + nx + 1] + W3 < m) m = d[k + nx + 1] + W3;
            if (x > 0 && d[k + nx - 1] + W3 < m) m = d[k + nx - 1] + W3;
          }
          if (y > 0) {
            if (d[k - nx] + W2 < m) m = d[k - nx] + W2;
            if (x < nx - 1 && d[k - nx + 1] + W3 < m) m = d[k - nx + 1] + W3;
            if (x > 0 && d[k - nx - 1] + W3 < m) m = d[k - nx - 1] + W3;
          }
        }
        d[i] = m;
      }
    }
  }

  // İşaret + birim: içi +, dışı −
  const b = hucre * olcek;
  for (let i = 0; i < toplam; i++) {
    d[i] = (doluluk[i] === 1 ? d[i] : -d[i]) * b;
  }
  return d;
}

export class FikoSPHVoxelKopru {
  private device: GPUDevice | null = null;
  private grid: IKopruGrid | null = null;
  private pipeline: GPUComputePipeline | null = null;
  private bgl: GPUBindGroupLayout | null = null;
  private bindGroup: GPUBindGroup | null = null;
  private bgPosBuffer: GPUBuffer | null = null; // bindGroup hangi pos buffer'la kuruldu
  private uniformBuffer: GPUBuffer | null = null;
  private fillTimeBuffer: GPUBuffer | null = null;
  private readbackBuffer: GPUBuffer | null = null;
  private uniformData = new ArrayBuffer(SPLAT_PARAMS_BOYUT);
  private toplamVoxel = 0;
  private radiusVoxel = 1;

  basla(device: GPUDevice, grid: IKopruGrid, radiusVoxel = 1): void {
    this.device = device;
    this.grid = grid;
    this.radiusVoxel = Math.max(0, Math.min(2, Math.round(radiusVoxel)));
    this.toplamVoxel = grid.boyut[0] * grid.boyut[1] * grid.boyut[2];

    this.uniformBuffer = device.createBuffer({
      label: 'Kopru Splat Params',
      size: SPLAT_PARAMS_BOYUT,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.fillTimeBuffer = device.createBuffer({
      label: 'Kopru FillTime',
      size: this.toplamVoxel * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
    });
    this.readbackBuffer = device.createBuffer({
      label: 'Kopru FillTime Readback',
      size: this.toplamVoxel * 4,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });
    this.sifirla();

    this.bgl = device.createBindGroupLayout({
      label: 'Kopru Splat BGL',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: 'Kopru Splat Pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      compute: {
        module: device.createShaderModule({ label: 'Kopru Splat Shader', code: VOXEL_SPLAT_PASS }),
        entryPoint: 'voxelSplat',
      },
    });
  }

  /** fillTime buffer'ını DOLMADI (0xFFFFFFFF) ile doldur */
  sifirla(): void {
    if (!this.device || !this.fillTimeBuffer) return;
    const init = new Uint32Array(this.toplamVoxel).fill(DOLMADI_U32);
    this.device.queue.writeBuffer(this.fillTimeBuffer, 0, init);
  }

  /**
   * Bir sim adımının splat pass'ini encode et. Uniform yazımı queue sıralı
   * olduğundan her adım AYRI submit edilmelidir (motor headless döngüsü öyle yapar).
   */
  encodeSplat(encoder: GPUCommandEncoder, posBuffer: GPUBuffer, activeCount: number, simTimeMs: number): void {
    if (!this.device || !this.pipeline || !this.grid || activeCount <= 0) return;

    if (!this.bindGroup || this.bgPosBuffer !== posBuffer) {
      this.bindGroup = this.device.createBindGroup({
        label: 'Kopru Splat BG',
        layout: this.bgl!,
        entries: [
          { binding: 0, resource: { buffer: this.uniformBuffer! } },
          { binding: 1, resource: { buffer: posBuffer } },
          { binding: 2, resource: { buffer: this.fillTimeBuffer! } },
        ],
      });
      this.bgPosBuffer = posBuffer;
    }

    const f = new Float32Array(this.uniformData);
    const u = new Uint32Array(this.uniformData);
    f[0] = this.grid.origin[0]; f[1] = this.grid.origin[1]; f[2] = this.grid.origin[2];
    f[3] = this.grid.cellSize;
    u[4] = this.grid.boyut[0]; u[5] = this.grid.boyut[1]; u[6] = this.grid.boyut[2];
    u[7] = activeCount >>> 0;
    u[8] = Math.max(0, Math.round(simTimeMs)) >>> 0;
    u[9] = this.radiusVoxel;
    this.device.queue.writeBuffer(this.uniformBuffer!, 0, this.uniformData);

    const pass = encoder.beginComputePass({ label: 'Kopru Voxel Splat' });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(activeCount / 256));
    pass.end();
  }

  /** fillTime (ms, u32) alanını GPU'dan oku */
  async fillTimeOku(): Promise<Uint32Array | null> {
    if (!this.device || !this.fillTimeBuffer || !this.readbackBuffer) return null;
    const encoder = this.device.createCommandEncoder({ label: 'Kopru Readback' });
    encoder.copyBufferToBuffer(this.fillTimeBuffer, 0, this.readbackBuffer, 0, this.toplamVoxel * 4);
    this.device.queue.submit([encoder.finish()]);
    await this.readbackBuffer.mapAsync(GPUMapMode.READ);
    const veri = new Uint32Array(this.readbackBuffer.getMappedRange().slice(0));
    this.readbackBuffer.unmap();
    return veri;
  }

  temizle(): void {
    this.uniformBuffer?.destroy();
    this.fillTimeBuffer?.destroy();
    this.readbackBuffer?.destroy();
    this.uniformBuffer = null;
    this.fillTimeBuffer = null;
    this.readbackBuffer = null;
    this.bindGroup = null;
    this.bgPosBuffer = null;
    this.pipeline = null;
    this.bgl = null;
    this.device = null;
    this.grid = null;
  }
}
